import {
  Box,
  FieldTitle,
  LabraToolTip,
  RadioButtons,
} from '@labrav/react-components'
import { updateProductPlanOnPrivateOffer } from '../../../modules/privateOffer/privateOfferCreation/action'
import type {
  KeyValue,
  OffersWithErrors,
} from '../../../modules/privateOffer/privateOfferCreation/types'
import { useStyles } from './AzurePricingSection.styles'
import clsx from 'clsx'
import { Typography } from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import { getKeyFromValue } from '../../../common/utils/getKeyFromValue'
import { useCallback, useMemo } from 'react'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import type { Product } from '../../../modules/productsListing/types'
import { useDispatch, useSelector } from '../../../store'
import { isEmpty } from 'lodash'
import {
  AzureOfferPlan,
  AzurePricingType,
} from '../../../modules/privateOffer/types'
import { AddUpdateButton } from '../../../common/components/AddUpdateButton'
import { PlanRow } from './PlanRow'
import { LoadingTypes, isLoading } from '@labrav/common'
import { pricingTypeMapping } from './utils'

export type AzurePricingSectionProps = {
  privateOfferWithError: OffersWithErrors
  product: Product
  handleDetailsChange: (data: KeyValue) => void
  addRow: (key: string, type?: string) => void
  deleteRow: (index: number, key: string) => () => void
}

export const AzurePricingSection = ({
  privateOfferWithError,
  handleDetailsChange,
  addRow,
  deleteRow,
  product,
}: AzurePricingSectionProps) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const { azurePricingType, plans, errors } = privateOfferWithError
  const plansError = (errors.plans as Array<Record<string, unknown>>) || []

  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )

  const planIds = useMemo(
    () =>
      (plans || [])
        .map(plan => plan.labraPlanId || '')
        .filter(id => !isEmpty(id)),
    [plans]
  )

  const handleUpdatePricingType = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleDetailsChange({
        key: 'azurePricingType',
        value: (event.target as HTMLInputElement).value,
      })
    },
    [azurePricingType]
  )

  const addPlan = () => {
    addRow('plans')
  }

  const updatePlanId = useCallback(
    async (
      index: number,
      plan: AzureOfferPlan,
      keyForErrorCheck?: string,
      subIndex?: number,
      meter?: string,
      deleteBillingTerms?: boolean
    ) => {
      await dispatch(
        updateProductPlanOnPrivateOffer(
          index,
          product?.productId,
          plan,
          keyForErrorCheck,
          subIndex,
          meter,
          deleteBillingTerms
        )
      )
    },
    [product?.productId]
  )

  const disabledAddNewPlan =
    loading ||
    (plans || []).length >= 10 ||
    (plans || []).length === (product.plans || []).length

  return (
    <div className={classes.textAlign}>
      <Box heading={'Pricing'} subHeading={''}>
        <div
          className={clsx(
            classes.subHeadingContainer,
            classes.companyDetailSubHead
          )}
        >
          <Typography
            className={clsx(classes.subHeading, classes.pricingTitle)}
            data-testid="start-date-heading"
          >
            Pricing Type <FieldTitle title="" isRequired={true} />
          </Typography>
          <LabraToolTip
            tooltipPlacement={'right'}
            label={
              "Choose 'discount' to offer a percentage discount or choose 'absolute' price to overwrite the existing price."
            }
          >
            <InfoOutlined className={classes.infoIcon} />
          </LabraToolTip>
        </div>
        <RadioButtons
          checkedValue={{
            key: getKeyFromValue(azurePricingType)(AzurePricingType) as string,
            value: azurePricingType || '',
          }}
          handleChange={handleUpdatePricingType}
          radioButtonMappings={pricingTypeMapping}
        />

        {azurePricingType ? (
          <>
            <div
              className={clsx(
                classes.subHeadingContainer,
                classes.companyDetailSubHead,
                classes.plans
              )}
            >
              <Typography
                className={classes.subHeading}
                data-testid="start-date-heading"
              >
                Plans
              </Typography>
              <LabraToolTip
                tooltipPlacement={'right'}
                label={
                  'Add plans to offer that customers can choose and purchase. Only public plans will be visible here for private offer creation.'
                }
              >
                <InfoOutlined className={classes.infoIcon} />
              </LabraToolTip>
            </div>

            {[...(plans || [])]?.map((planRow, index) => (
              <PlanRow
                azurePricingType={azurePricingType}
                planRow={planRow}
                index={index}
                planRowError={plansError[index]}
                deleteRow={deleteRow}
                product={product}
                updatePlanId={updatePlanId}
                planIds={planIds}
              />
            ))}

            <div className={classes.addBtncontainer}>
              <AddUpdateButton
                label={`Add ${(plans || []).length ? 'another' : ''} plan`}
                disabled={disabledAddNewPlan}
                type={'add'}
                icon={AddCircleOutlineIcon}
                onClick={addPlan}
              />
            </div>
          </>
        ) : (
          <></>
        )}
      </Box>
    </div>
  )
}
